import React from 'react';
import { FONT_BODY } from './theme';
import { LEGAL } from './data';
import { Logo, IconCloseButton } from './ui';

export type LegalKey = 'terms' | 'privacy';

const LegalDrawer: React.FC<{
  docKey: LegalKey;
  bodyRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onTerms: () => void;
  onPrivacy: () => void;
  onContact: () => void;
}> = ({ docKey, bodyRef, onClose, onTerms, onPrivacy, onContact }) => {
  const doc = LEGAL[docKey];
  const tab = (on: boolean) => ({ fg: on ? '#2563EB' : '#64748B', line: on ? '#2563EB' : 'transparent' });
  const tabT = tab(docKey === 'terms');
  const tabP = tab(docKey === 'privacy');

  const jump = (i: number) => {
    const b = bodyRef.current;
    const el = b && b.querySelector(`#legal-${i}`);
    if (el && b) b.scrollTo({ top: (el as HTMLElement).offsetTop - 12, behavior: 'smooth' });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 110,
        background: 'rgba(15,23,42,.45)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        justifyContent: 'flex-end',
        animation: 'sm1p-fadein .2s ease both',
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={doc.title}
        style={{
          width: 'min(660px,100%)',
          height: '100%',
          background: '#fff',
          boxShadow: '-30px 0 80px -30px rgba(15,23,42,.45)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'sm1p-slideover .34s cubic-bezier(.2,.7,.2,1) both',
        }}
      >
        <div style={{ flex: 'none', padding: '16px clamp(16px,3vw,28px) 0', borderBottom: '1px solid #EDF3FC' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo width={98} height={18} />
            <span style={{ font: `700 13.5px ${FONT_BODY}`, color: '#0F172A' }}>Legal</span>
            <IconCloseButton onClick={onClose} size={34} label="Close" />
          </div>
          <div role="tablist" style={{ display: 'flex', gap: 4, margin: '14px 0 0' }}>
            <button
              role="tab"
              type="button"
              onClick={onTerms}
              aria-selected={docKey === 'terms'}
              className="sm1p-tab"
              style={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                padding: '10px 12px 12px',
                font: `700 14px ${FONT_BODY}`,
                color: tabT.fg,
                boxShadow: `inset 0 -2px 0 ${tabT.line}`,
              }}
            >
              Terms &amp; Conditions
            </button>
            <button
              role="tab"
              type="button"
              onClick={onPrivacy}
              aria-selected={docKey === 'privacy'}
              className="sm1p-tab"
              style={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                padding: '10px 12px 12px',
                font: `700 14px ${FONT_BODY}`,
                color: tabP.fg,
                boxShadow: `inset 0 -2px 0 ${tabP.line}`,
              }}
            >
              Privacy Policy
            </button>
          </div>
        </div>
        <div
          ref={bodyRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            position: 'relative',
            padding: 'clamp(20px,3vw,30px) clamp(16px,3vw,28px) 40px',
          }}
        >
          <h2
            style={{
              font: `800 clamp(22px,2.8vw,28px)/1.2 ${FONT_BODY}`,
              letterSpacing: '-.03em',
              color: '#0F172A',
              margin: 0,
            }}
          >
            {doc.title}
          </h2>

          {doc.secs.length > 0 && (
            <>
              <p style={{ font: `500 13px/1.6 ${FONT_BODY}`, color: '#64748B', margin: '8px 0 0' }}>{doc.intro}</p>
              <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '18px 0 6px' }}>
                {doc.secs.map((s, i) => (
                  <button
                    key={s.h}
                    type="button"
                    onClick={() => jump(i)}
                    className="sm1p-jump"
                    style={{
                      border: '1px solid #E3EBF7',
                      background: '#F7FAFF',
                      cursor: 'pointer',
                      borderRadius: 999,
                      padding: '6px 11px',
                      font: `600 12px ${FONT_BODY}`,
                      color: '#334155',
                    }}
                  >
                    {s.h}
                  </button>
                ))}
              </nav>
              {doc.secs.map((s, i) => (
                <section key={s.h} id={`legal-${i}`} style={{ padding: '22px 0 0', margin: '18px 0 0', borderTop: '1px solid #F1F6FD' }}>
                  <h3
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'baseline',
                      font: `700 16px/1.35 ${FONT_BODY}`,
                      color: '#0F172A',
                      margin: 0,
                    }}
                  >
                    <span style={{ font: `700 12px ${FONT_BODY}`, color: '#2563EB', minWidth: 20 }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.h}
                  </h3>
                  {s.p.map((para, j) => (
                    <p
                      key={j}
                      style={{
                        font: `400 14px/1.65 ${FONT_BODY}`,
                        color: '#475569',
                        margin: '8px 0 0 30px',
                        textWrap: 'pretty',
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </section>
              ))}
            </>
          )}

          {doc.secs.length === 0 && (
            <div style={{ margin: '20px 0 0', background: '#F7FAFF', border: '1px dashed #BFD6F6', borderRadius: 14, padding: 20 }}>
              <p style={{ font: `700 14px ${FONT_BODY}`, color: '#0F172A', margin: 0 }}>Approved text pending</p>
              <p style={{ font: `400 13.5px/1.6 ${FONT_BODY}`, color: '#475569', margin: '8px 0 0', textWrap: 'pretty' }}>
                The concise {doc.title} will be condensed from the published Soomuch.AI document. It stays empty until
                that source text is supplied, so no provision here is invented.
              </p>
            </div>
          )}

          <p
            style={{
              font: `400 13px/1.6 ${FONT_BODY}`,
              color: '#64748B',
              margin: '30px 0 0',
              paddingTop: 16,
              borderTop: '1px solid #EDF3FC',
            }}
          >
            Questions about this document?{' '}
            <button
              type="button"
              onClick={onContact}
              style={{
                border: 0,
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                font: `600 13px ${FONT_BODY}`,
                color: '#2563EB',
              }}
            >
              Talk to Soomuch.AI
            </button>
          </p>
        </div>
      </aside>
    </div>
  );
};

export default LegalDrawer;
